import React, { useEffect, useState } from "react";
import "./ExpenseGraph.css";
import ExpenseModal from "./ExpenseModal";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const ExpenseGraph = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenseData, setExpenseData] = useState([]);
  const [root, setRoot] = useState(null);

  const fetchExpenses = async () => {
    try {
      const response = await fetch('https://jetpriyanshubackend.vercel.app/sangrur_get_expenses');
      const data = await response.json();
      const sortedData = (data || []).sort((a, b) => new Date(b.Date) - new Date(a.Date));
      setExpenseData(sortedData);
      return sortedData;
    } catch (error) {
      setExpenseData([]);
      console.error('Error fetching expenses:', error);
      return [];
    }
  };
  
  const handleAddExpense = async (newExpense) => {
    try {
      const response = await fetch(
        "https://jetpriyanshubackend.vercel.app/sangrur_add_expense",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Date: newExpense.date,
            Amount: parseInt(newExpense.amount),
            Category: newExpense.category,
          }),
        }
      );

      if (response.ok) {
        setIsModalOpen(false);
        const updatedData = await fetchExpenses();
        updateChartData(updatedData);
      }
    } catch (error) {
      console.error("Error saving expense:", error);
    }
  };

  const updateChartData = (expenses) => {
    if (root) {
      const series = root.container.children.getIndex(0).series.getIndex(0);
      const formattedData = expenses
        .map((expense) => ({
          ...expense,
          Date: new Date(expense.Date).getTime(),
        }))
        .sort((a, b) => a.Date - b.Date);

      series.data.setAll(formattedData);
    }
  };

  useEffect(() => {
    let chartRoot = null;
    const chartDiv = document.getElementById("chartdiv");

    if (chartDiv) {
      const initializeChart = async () => {
        am5.array.each(am5.registry.rootElements, function (r) {
          if (r.dom.id === "chartdiv") {
            r.dispose();
          }
        });

        chartRoot = am5.Root.new("chartdiv");
        setRoot(chartRoot);

        chartRoot.setThemes([am5themes_Animated.new(chartRoot)]);

        const chart = chartRoot.container.children.push(
          am5xy.XYChart.new(chartRoot, {
            panX: true,
            panY: true,
            wheelX: "panX",
            wheelY: "zoomX",
            pinchZoomX: true,
          })
        );

        const xAxis = chart.xAxes.push(
          am5xy.DateAxis.new(chartRoot, {
            maxDeviation: 0.2,
            baseInterval: { timeUnit: "day", count: 1 },
            renderer: am5xy.AxisRendererX.new(chartRoot, {}),
            tooltip: am5.Tooltip.new(chartRoot, {}),
          })
        );

        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(chartRoot, {
            maxDeviation: 0.2,
            renderer: am5xy.AxisRendererY.new(chartRoot, {
              minGridDistance: 20,
            }),
            numberFormat: "₹#,###",
          })
        );

        const series = chart.series.push(
          am5xy.LineSeries.new(chartRoot, {
            name: "Expenses",
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: "Amount",
            valueXField: "Date",
            tooltip: am5.Tooltip.new(chartRoot, {
              labelText: "Daily Total: ₹{valueY}",
            }),
          })
        );

        series.bullets.push(function () {
          return am5.Bullet.new(chartRoot, {
            sprite: am5.Label.new(chartRoot, {
              text: "₹{valueY}",
              centerY: am5.p100,
              centerX: am5.p50,
              populateText: true,
              dy: -15,
            }),
          });
        });

        series.strokes.template.setAll({
          strokeWidth: 3,
          templateField: "strokeSettings",
        });

        series.bullets.push(() => {
          return am5.Bullet.new(chartRoot, {
            sprite: am5.Circle.new(chartRoot, {
              strokeWidth: 3,
              stroke: series.get("stroke"),
              radius: 5,
              fill: am5.color(0xffffff),
            }),
          });
        });

        chart.set(
          "scrollbarX",
          am5.Scrollbar.new(chartRoot, {
            orientation: "horizontal",
          })
        );

        chart.set(
          "scrollbarY",
          am5.Scrollbar.new(chartRoot, {
            orientation: "vertical",
          })
        );

        chart.set(
          "cursor",
          am5xy.XYCursor.new(chartRoot, {
            behavior: "zoomXY",
            xAxis: xAxis,
            yAxis: yAxis,
          })
        );

        chart.get("scrollbarX").toFront();
        chart.get("scrollbarY").toFront();

        const expenses = await fetchExpenses();
        const dailyTotals = expenses.reduce((acc, expense) => {
          const dateKey = new Date(expense.Date).toDateString();
          if (!acc[dateKey]) {
            acc[dateKey] = {
              Date: new Date(expense.Date).getTime(),
              Amount: 0,
              Category: "Daily Total",
            };
          }
          acc[dateKey].Amount += expense.Amount;
          return acc;
        }, {});

        const formattedData = Object.values(dailyTotals).sort(
          (a, b) => a.Date - b.Date
        );
        series.data.setAll(formattedData);

        series.appear(1000);
        chart.appear(1000, 100);
      };

      initializeChart();
    }

    return () => {
      if (chartRoot) {
        chartRoot.dispose();
      }
    };
  }, []);

  const monthTotal = expenseData.reduce((total, expense) => {
    const expenseDate = new Date(expense.Date);
    const currentDate = new Date();
    if (expenseDate.getMonth() === currentDate.getMonth() && 
        expenseDate.getFullYear() === currentDate.getFullYear()) {
      return total + expense.Amount;
    }
    return total;
  }, 0);
  

  return (
    <div className="expense-container">
      <div className="current-total">Month's Total: ₹{monthTotal}</div>
      <button
        className="add-expense-button"
        onClick={() => setIsModalOpen(true)}
      >
        +
      </button>
      <div id="chartdiv"></div>

<div className="expense-table-container">
      <table className="expense-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Amount</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {expenseData.map((expense, index) => (
            <tr key={index}>
              <td>{expense.Category}</td>
              <td>₹{expense.Amount}</td>
              <td>{new Date(expense.Date).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <ExpenseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddExpense}
      />
    </div>
  );
};

export default ExpenseGraph;
